import MomentUtils from '@date-io/moment';
import { Box, Button, MenuItem, Paper, Select, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment, { Moment } from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import StoryItem from '../../components/StoryItem';
import { SubmitControl } from '../../components/SubmitControl';
import { useStoryPriceSettingScheduleMutation } from '../../hooks/queries/useStoryPriceSettingSchedule';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import useStorySelect from '../../hooks/useStorySelect';
import settings from '../../services/settings';
import { IStory } from '../../services/types';
import { RouteNames } from '../config';

const useStyles = makeStyles((theme: Theme) => ({
  sectionWrap: {
    paddingLeft: 8,
    marginBottom: 24,
    width: 530,
  },

  storyPlaceholder: {
    width: 400,
    height: 76,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 24,
  },

  selectBox: {
    width: 240,
  },
}));

const SaleStoryCreate = (): JSX.Element => {
  const classes = useStyles();

  const storySelect = useStorySelect();
  const confirm = useConfirmDialog();
  const history = useHistory();
  const { create } = useStoryPriceSettingScheduleMutation();

  const [validationMessages, setValidationMessages] = useState<Set<string>>(new Set<string>());
  const [story, setStory] = useState<IStory>();
  const [price, setPrice] = useState<number>(3);
  const [startsAt, setStartsAt] = useState<Moment>(moment().startOf('hour'));

  const scopeRef = useRef<HTMLDivElement>();

  const reset = () => {
    setStory(undefined);
    setPrice(3);
    setStartsAt(moment());
  };

  const handleSubmitClick = async () => {
    if (!story) {
      return;
    }

    await create.mutateAsync({ storyId: story.id, price, startsAt: startsAt.utc().format() });
    history.push(RouteNames.SaleStoryScheduling);
    reset();
  };

  const handleCancelClick = () => {
    confirm.open({
      text: 'Discard any changes?',
      onProceed: reset,
    });
  };

  const handleSelectStoryClick = () => {
    storySelect.show({
      title: 'Select Story',
      onSelect: (story: IStory) => setStory(story),
      scopeRef,
    });
  };

  const handleStoryItemClick = () => {
    if (!story) {
      return;
    }

    window.open(`${settings.getCrewURI()}/stories/${story.id}`, '_blank', 'noopener noreferrer');
  };

  const handlePriceChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setPrice(event.target.value as number);
  };

  const handleStartsAtChange = (date: MaterialUiPickersDate) => {
    if (date) {
      setStartsAt(date);
    }
  };

  useEffect(() => {
    const messageSet = new Set<string>();

    if (!story) {
      messageSet.add('Select a story.');
    }

    if (price === undefined) {
      messageSet.add('The price should be selected.');
    }

    if (!startsAt) {
      messageSet.add('Select a schedule start time.');
    }

    setValidationMessages(messageSet);
  }, [story, price, startsAt]);

  return (
    // eslint-disable-next-line
    // @ts-ignore due to: https://github.com/mui-org/material-ui/issues/17010
    <Box ref={scopeRef}>
      <Box display="flex" alignItems="center" paddingBottom={2}>
        <Typography variant="h6">Create Story Price Sale Schedule</Typography>
        <SubmitControl
          mode="create"
          validationMessages={[...validationMessages]}
          onSubmitClick={handleSubmitClick}
          onCancelClick={handleCancelClick}
        />
      </Box>

      <Box className={classes.sectionWrap}>
        <Button color="primary" variant="contained" onClick={handleSelectStoryClick}>
          Select a story
        </Button>

        <Paper className={classes.storyPlaceholder}>
          {!story && <Typography variant="caption">To set the schedule, select a story first.</Typography>}
          {story && (
            <StoryItem story={story} readonly={true} onItemClick={handleStoryItemClick} extraText={`Episode price = ${story.episodePrice || 3}`} />
          )}
        </Paper>
      </Box>

      <Box className={classes.sectionWrap}>
        <Typography variant="subtitle1">Episode Price</Typography>
        <Select value={price} onChange={handlePriceChange} className={classes.selectBox}>
          <MenuItem value={3}>3 Coins</MenuItem>
          <MenuItem value={2}>2 Coins (33% OFF)</MenuItem>
          <MenuItem value={1}>1 Coin (66% OFF)</MenuItem>
        </Select>
      </Box>

      <Box className={classes.sectionWrap}>
        <Typography variant="subtitle1">Scheduling</Typography>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDateTimePicker
            label="Scheduling starts at"
            format="llll"
            required
            margin="dense"
            value={startsAt.format()}
            minDate={moment()
              .startOf('hour')
              .format()}
            onChange={handleStartsAtChange}
            className={classes.selectBox}
          />
        </MuiPickersUtilsProvider>
      </Box>
    </Box>
  );
};

export default SaleStoryCreate;
